import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../../constants/endpoints';
import { FragmentNames, NutritionFastingViewAliases } from '../../../../../codegen';
import BaseController from '../../../../../controllers/BaseController';
import InformationBarUtils from '../../../../../controllers/commons/header/information-bar-header-utils';
import { FastingTypes } from './fasting-types';
import moment from 'moment';
import { ISO_DATE_INPUT } from '../../../../../constants/date-utils';
import { DurationType } from '../../cpoe-view-types';

@Controller({ fragment: FragmentNames.NUTRITION_FASTING_VIEW })
class FastingViewController extends BaseController {

  @Inject(NutritionFastingViewAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(NutritionFastingViewAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(NutritionFastingViewAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(NutritionFastingViewAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodValidityAPI!: FormAPI;

  @Inject(NutritionFastingViewAliases.FORM_ADDITIONAL_INFORMATION)
  formAddicionalInformationsAPI!: FormAPI;

  @Inject(NutritionFastingViewAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(NutritionFastingViewAliases.FIELD_OBSERVATION)
  fieldObservationAPI!: FieldAPI;

  @Inject(NutritionFastingViewAliases.FIELD_JUSTIFICATION)
  fieldJustificationAPI!: FieldAPI;

  @Inject(NutritionFastingViewAliases.FIELD_PRESCRIBER_DATE)
  fieldPrescriberDtAPI!: FieldAPI;

  @Inject(NutritionFastingViewAliases.FIELD_PRESCRIBER_NAME)
  fieldPrescriberNameAPI!: FieldAPI;

  @Inject(NutritionFastingViewAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(NutritionFastingViewAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @OnMount(NutritionFastingViewAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { nutritionFastingId, patientId  } = this.historyService.getCurrentRouteParams()
    this.fetchFastingDetails(String(nutritionFastingId), String(patientId))
  }

  @OnMount(NutritionFastingViewAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormInformations(): void {
    const fastingView = this.context.get('fastingView') as FastingTypes
    if(fastingView) {
      const { type, objective } = fastingView;
      this.formMainInformationsAPI.setValue({
        objective,
        type
      })
    }
  }

  @OnMount(NutritionFastingViewAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const fastingView = this.context.get('fastingView') as FastingTypes

    if(fastingView) {
      const {duration, durationType } = fastingView
      const endDate = moment(String(fastingView.endDate)).utc().format(ISO_DATE_INPUT)
      const startDate = moment(String(fastingView.startDate)).utc().format(ISO_DATE_INPUT)

      this.formPeriodValidityAPI.setValue({
        duration,
        endDate,
        startDate,
      })

      this.inputStartDateAPI.setValue(startDate)
      this.inputEndDateAPI.setValue(endDate)
      this.showFieldsByDurationType(durationType);
    }
  }

  @OnMount(NutritionFastingViewAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAddicionalInformations(): void {
    const fastingView = this.context.get('fastingView') as FastingTypes
    if(fastingView) {
      const { prescriberDt, prescriberName, justification, observation } = fastingView

      this.formAddicionalInformationsAPI.setValue({
        justification,
        observation,
        prescriberDt,
        prescriberName,
      })

      this.fieldJustificationAPI.setVisible(!!justification)
      this.fieldPrescriberNameAPI.setVisible(!!prescriberName)
      this.fieldPrescriberDtAPI.setVisible(!!prescriberDt)
      this.fieldObservationAPI.setVisible(!!observation)
    }
  }

  private fetchFastingDetails(nutritionId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_NUTRITION_FASTING_DETAILS, { nutritionId, patientId })
      .then((response: HttpResponse) => {
        const fastingView = response.body as FastingTypes;
        this.context.set({ fastingView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'nutrition-fasting',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default FastingViewController;